import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import MilestoneForm from '../../components/Configuration/MilestoneForm';
import { getMilestones, deleteMilestone } from '../../services/milestonesService';

const Milestones = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadMilestones = async () => {
    setLoading(true);
    try {
      const res = await getMilestones();
      if (res.success && res.data) setMilestones(res.data);
      else setToast({ isOpen: true, message: 'Error al cargar hitos', type: 'error' });
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar hitos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMilestones();
  }, []);

  const filtered = milestones.filter((m) => {
    const matchSearch =
      !searchTerm ||
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (m.project && (m.project.name?.toLowerCase().includes(searchTerm.toLowerCase()) || m.project.code?.toLowerCase().includes(searchTerm.toLowerCase())));
    const matchProject = !projectFilter || String(m.project_id) === projectFilter;
    return matchSearch && matchProject;
  });

  const projectsForFilter = milestones
    .map((m) => m.project)
    .filter(Boolean);
  const uniqueProjects = [];
  const seen = new Set();
  projectsForFilter.forEach((p) => {
    if (p && !seen.has(p.id)) {
      seen.add(p.id);
      uniqueProjects.push(p);
    }
  });
  uniqueProjects.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const handleDeleteClick = (milestone) => {
    setMilestoneToDelete(milestone);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!milestoneToDelete) return;
    try {
      const res = await deleteMilestone(milestoneToDelete.id);
      if (res.success) {
        setMilestones((prev) => prev.filter((m) => m.id !== milestoneToDelete.id));
        setToast({ isOpen: true, message: 'Hito eliminado', type: 'success' });
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al eliminar', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al eliminar',
        type: 'error',
      });
    } finally {
      setIsDeleteModalOpen(false);
      setMilestoneToDelete(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-PE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Hitos</h1>
              <p className="text-gray-600 mt-1">Configure los hitos de proyecto con nombre y fecha</p>
            </div>
            <button
              onClick={() => {
                setEditingMilestone(null);
                setIsFormOpen(true);
              }}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
            >
              + Nuevo Hito
            </button>
          </div>
          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/configuration" className="hover:text-teal-600">Configuración</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Hitos</span>
          </nav>
          <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-wrap gap-2">
            <input
              type="text"
              placeholder="Buscar por nombre o proyecto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            />
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todos los proyectos</option>
              {uniqueProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} – {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Total de Hitos</div>
            <div className="text-2xl font-bold text-gray-800">{filtered.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="text-sm text-gray-600">Proyectos con hitos</div>
            <div className="text-2xl font-bold text-teal-600">{uniqueProjects.length}</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm || projectFilter
                ? 'No hay hitos con los filtros aplicados'
                : 'No hay hitos. Cree uno asignando un proyecto, nombre y fecha.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proyecto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hito
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha inicio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha fin
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.map((m) => (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-teal-600">
                          {m.project?.code || '-'}
                        </span>
                        <span className="text-sm text-gray-700 ml-1">
                          {m.project?.name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900">{m.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(m.due_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(m.end_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                        <button
                          onClick={() => {
                            setEditingMilestone(m);
                            setIsFormOpen(true);
                          }}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteClick(m)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <MilestoneForm
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingMilestone(null);
          }}
          milestone={editingMilestone}
          onSuccess={() => {
            loadMilestones();
            setToast({
              isOpen: true,
              message: editingMilestone ? 'Hito actualizado' : 'Hito creado',
              type: 'success',
            });
          }}
        />
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setMilestoneToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Eliminar hito"
          message={
            milestoneToDelete
              ? `¿Eliminar el hito "${milestoneToDelete.name}" (${formatDate(milestoneToDelete.due_date)})?`
              : ''
          }
          confirmText="Eliminar"
          cancelText="Cancelar"
        />
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default Milestones;
